<template>
  <div class="ls-service-event-item">
    <div class="ls-service-event-item--upper-wrapper">
      <LsIcon
        v-if="!isQuote"
        :size="screenSize.desktop ? 'sm' : 'xlg'"
        :type="icon.name"
        :color="icon.color"
        test-id="icon"
        class="ls-service-event-item--icon"
      />
      <div
        v-if="isLtrsProvidedByPartner && !isLtrsServiceWindowConfirmed"
        class="ls-service-event-item--text-strong"
      >
        {{ t('lawnTreatment.pendingConfirmation') }}
      </div>
      <div
        v-if="!isLtrsProvidedByPartner || isLtrsServiceWindowConfirmed"
        class="ls-service-event-item--text-wrapper"
      >
        <p class="ls-service-event-item--status-text" test-id="status-text">{{ statusText }}</p>
        <p v-if="!dateOverrideText" class="ls-service-event-item--date-text" test-id="date-text">
          {{ renderDateRange }}
        </p>
        <p
          v-if="dateOverrideText"
          class="ls-service-event-item--date-override-text"
          test-id="date-override-text"
        >
          {{ dateOverrideText }}
        </p>
      </div>
    </div>
    <p v-if="isMowing" class="ls-service-event-item--mowing-info">
      Our lawn mowing service includes mowing your lawn, edging areas that border driveways and
      walkways, and blowing clippings off all hard surfaces.
    </p>
    <b-message
      v-if="renderScheduleDetailAlert.type"
      :type="renderScheduleDetailAlert.type"
      class="ls-service-event-item--schedule-detail-alert"
    >
      <span class="has-text-weight-bold">{{ renderScheduleDetailAlert.header }}</span>
      <p>{{ renderScheduleDetailAlert.message }}</p>
      <LsButton
        v-if="schedule.is_paused && schedule.pause_can_resume"
        text="Reactivate Earlier"
        bg-color="primary"
        track-id="ls_service_event_item-reactivate_earlier"
        class="ls-service-event-item--reactivate-earlier-button"
        @click.stop="reactivateEarlier"
      />
    </b-message>
  </div>
</template>

<script>
import VueTypes from 'vue-types';
import { lsDateHelpers, lsFilters, lsModalService } from 'ls-vue-common';
import { LsIcon, LsButton } from '@/components/common';
import { ResumePausedScheduleModal } from '@/modals/';
import { mapState } from 'vuex';
import moment from 'moment';
import _ from 'lodash';

export default {
  name: 'LsServiceEventItem',
  components: {
    LsIcon,
    LsButton,
  },
  props: {
    icon: VueTypes.shape({
      color: VueTypes.string.isRequired,
      name: VueTypes.string.isRequired,
    }).isRequired,
    statusText: VueTypes.string.isRequired,
    dateOverrideText: VueTypes.string,
    startDate: VueTypes.string,
    endDate: VueTypes.string,
    isLtrsProvidedByPartner: VueTypes.bool.def(false),
    isLtrsServiceWindowConfirmed: VueTypes.bool.def(false),
    isMowing: VueTypes.bool.def(false),
    isQuote: VueTypes.bool.def(false),
    schedule: VueTypes.shape({
      is_paused: VueTypes.bool,
      next_in_paused_season: VueTypes.bool,
      status: VueTypes.string,
      next_ends_at: VueTypes.string,
      pause_can_resume: VueTypes.bool,
    }).loose.def(_.constant({})),
  },
  computed: {
    ...mapState(['screenSize']),
    renderDateRange() {
      return lsDateHelpers.getDateRange({
        startDate: this.startDate,
        endDate: this.endDate,
      });
    },
    renderScheduleDetailAlert() {
      let header = '';
      let message = '';
      let type = '';

      if (this.schedule.is_paused && this.schedule.next_in_paused_season) {
        header = 'Paused with upcoming jobs';
        message =
          'This service has been paused, but there are one or more upcoming jobs scheduled in order to meet policy requirements.';
        type = 'is-warning';
      }

      if (this.schedule.is_paused && !this.schedule.next_in_paused_season) {
        header = 'Paused';
        message = `This service has been paused and will automatically resume ${
          this.schedule.next_starts_at
            ? `on ${lsFilters.date({
                date: this.schedule.next_starts_at,
                format: 'year',
              })}`
            : `in the spring.`
        }`;
        type = 'is-warning';
      }

      if (
        this.schedule.status === 'cancelled' &&
        this.schedule.next_ends_at &&
        moment().isBefore(this.schedule.next_ends_at)
      ) {
        header = 'Cancelled with upcoming jobs';
        message =
          'This service has been cancelled, but there are one or more upcoming jobs scheduled in order to meet policy requirements.';
        type = 'is-danger';
      }

      return {
        header,
        message,
        type,
      };
    },
  },
  methods: {
    reactivateEarlier() {
      lsModalService.open({
        parent: this,
        component: ResumePausedScheduleModal,
        props: {
          schedule_id: this.schedule.schedule_id,
          nextStartDate: this.startDate,
          property_id: this.schedule.property_id,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ls-service-event-item--upper-wrapper {
  display: flex;
  align-items: center;
}

.ls-service-event-item--text-wrapper {
  display: flex;
}

.ls-service-event-item--text-strong {
  color: $primary;
  font-size: 1.2rem;
  font-weight: 700;
}

.ls-service-event-item--status-text {
  font-size: $size-5;
  padding-right: 4px;
}

.ls-service-event-item--date-text {
  font-weight: 700;
}

.ls-service-event-item--date-override-text {
  font-weight: 700;
  margin-left: 0.5rem;
}

.ls-service-event-item--mowing-info {
  padding: 4px 0 0 22px;
  color: $gray-secondary;
  font-style: italic;
}

.ls-service-event-item--icon {
  margin-right: 6px;
}

.ls-service-event-item--schedule-detail-alert {
  margin-top: 10px;
}

.ls-service-event-item--reactivate-earlier-button {
  display: flex;
  margin-left: auto;
  margin-top: 10px;
}

@media (max-width: $mobile-max-width) {
  .ls-service-event-item--text-wrapper {
    display: inline;
  }

  .ls-service-event-item--date-override-text {
    font-weight: 700;
    margin: 0;
    font-size: 1.5rem;
  }

  .ls-service-event-item--mowing-info {
    padding-left: 0px;
  }

  .ls-service-event-item--icon {
    margin-right: 12px;
  }
}

.themed-app {
  .ls-service-event-item {
    .ls-service-event-item--status-text {
      font-size: var(--theme-txt-body);
    }
    .ls-service-event-item--mowing-info {
      color: $theme-color-gray-darker;
    }
  }
}
</style>
